import React from "react";

const IK =
  "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course";

const whatCards = [
  {
    title: "Yesterday's Accountant",
    body: "Prepared reports, reconciled data manually, tested small samples, and constantly chased data.",
    borderColor: "#F33",
  },
  {
    title: "Tomorrow's CPA",
    body: "Builds AI workflows, leverages specialized AI agents, audits full populations, explains exceptions, and owns strategic judgment.",
    borderColor: "#2A85FF",
  },
];

const newHiringMetric = {
  title: "The New Hiring Metric",
  body: "Modern firms and multinational employers are specifically looking for candidates who can answer one question: “Can you help us implement AI across our audit, tax, and finance workflows?”",
  borderColor: "#2A85FF",
};

type WhyCardData = {
  title: string;
  desc: React.ReactNode;
  img: string;
  /*
   * Grid placement. The reading order differs between breakpoints — mobile
   * runs Global Recognition, U.S. & India, Pass Rates, STEM, Salaries, while
   * desktop runs in source order — so mobile order is set explicitly and
   * reset at md. This replaces rendering three of the cards twice behind
   * `hidden lg:block` / `block lg:hidden`, which duplicated their markup and
   * their background image requests.
   */
  layout: string;
};

const whyCards: WhyCardData[] = [
  {
    title: "Global Recognition",
    desc: "One of the most (if not the most) respected accounting credentials",
    img: `${IK}/Global_recognition.webp`,
    layout: "order-1 md:order-none",
  },
  {
    title: "Unmatched Pass Rates",
    desc: (
      <>
        Miles delivers an impressive{" "}
        <strong className="font-bold">~82% pass rate</strong> versus the ~47%
        global average. What&apos;s more, nearly 96% of our candidates who clear
        their first exam go on to earn their full CPA license
      </>
    ),
    img: `${IK}/Unmatched_Pass_Rates.webp`,
    // Two columns wide at both sizes: the full width of mobile's 2-column
    // grid, and the wide slot beside Global Recognition on desktop's 3-column.
    layout: "col-span-2 order-3 md:order-none",
  },
  {
    title: "U.S. & India Opportunities",
    desc: (
      <>
        Partnering directly with the{" "}
        <strong className="font-bold">Big 4 and 220+ top MNCs</strong> who are
        actively hiring AI-Ready CPAs.
      </>
    ),
    img: `${IK}/India_opportunities.webp`,
    layout: "order-2 md:order-none",
  },
  {
    title: "STEM Advantage",
    desc: "Indian CPAs can work in the U.S. for up to 3 years via the STEM OPT route.",
    img: `${IK}/STEM_Advantage.webp`,
    layout: "order-4 md:order-none",
  },
  {
    title: "Higher Salaries",
    desc: "CPAs typically earn 30-40% more than non-CPAs",
    img: `${IK}/Higher_salaries.webp`,
    layout: "order-5 md:order-none",
  },
];

/* Hand-drawn vertical rule between the two desktop columns. The artwork is a
   1x467 stroke; preserveAspectRatio="none" lets it stretch to whatever height
   the taller column ends up being, so it never falls short or overshoots. */
const ColumnDivider = () => (
  <div className="hidden lg:block self-stretch" aria-hidden="true">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1 467"
      preserveAspectRatio="none"
      fill="none"
      className="h-full w-px"
      focusable="false"
    >
      <path
        d="M0.774358 261.405C0.70687 271.705 0.987547 279.814 0.932954 292.369C0.879212 304.924 0.738347 306.804 0.75506 321.182C0.767014 335.56 0.665261 349.946 0.704206 356.348C0.739072 362.75 0.603861 363.425 0.62302 372.516C0.639213 381.607 0.61151 387.116 0.569672 395.067C0.524076 403.018 0.496553 427.444 0.506405 436.326C0.510703 445.208 0.472143 455.339 0.471673 461.432C0.470168 463.329 0.469259 464.823 0.468903 465.968C0.468741 466.453 0.468725 466.677 0.46873 466.677C0.468745 466.677 0.468663 466.329 0.468441 465.706C0.467288 462.68 0.465973 458.158 0.454332 452.815C0.427367 442.854 0.445695 441.765 0.418201 428.313C0.388724 414.862 0.317986 406.78 0.32808 390.953C0.352413 375.126 0.150611 371.49 0.23141 349.149C0.335724 326.807 0.0893709 324.456 0.0180007 302.192C-0.0514251 279.928 0.100688 251.825 0.0880466 238.404C0.0862584 236.781 0.0842061 235.09 0.082077 233.338C0.0662747 220.608 0.0482814 204.656 0.0934836 187.931C0.140937 168.905 0.0793025 145.419 0.171818 124.417C0.261207 103.416 0.359622 73.7242 0.397862 60.0071C0.438135 46.2906 0.437328 42.9473 0.439841 29.7444C0.441167 22.1553 0.462248 11.7005 0.467663 2.86956C0.470158 -1.57472 0.467095 -0.56764 0.471254 3.90568C0.477977 10.4471 0.479362 15.8169 0.493397 19.1875C0.523625 25.9224 0.552845 28.7849 0.546837 37.2874C0.535413 45.7894 0.606406 49.7956 0.611969 54.7518C0.616118 59.7081 0.562829 59.6266 0.647569 84.8275C0.739914 110.029 0.784575 123.844 0.814198 133.388C0.847702 142.931 0.790217 162.859 0.831253 178.412C0.871419 192.208 0.862778 218.645 0.894384 233.338C0.898589 235.211 0.903582 236.892 0.909625 238.333C0.962471 251.078 0.842805 251.105 0.774358 261.405Z"
        fill="#ADADAD"
      />
    </svg>
  </div>
);

const InfoCard = ({
  title,
  body,
  borderColor,
}: {
  title: string;
  body: string;
  borderColor: string;
}) => (
  <div
    className="bg-[#F6F6F6] p-4 shadow-sm"
    style={{ borderLeft: `4px solid ${borderColor}` }}
  >
    <p className="mb-1.5 text-sm font-bold text-[#000] tracking-[-0.42px]">
      {title}
    </p>
    <p className="text-sm text-[#333] tracking-[-0.42px]">{body}</p>
  </div>
);

const WhyCard = ({ card }: { card: WhyCardData }) => (
  <div
    className={`relative flex h-[200px] w-full flex-col justify-end p-[10px] ${card.layout}`}
    style={{
      borderRadius: "8px",
      background: `linear-gradient(180deg, rgba(0, 120, 255, 0.00) 0%, #0B2A4D 79.21%), url(${card.img}) lightgray 50% / cover no-repeat`,
    }}
  >
    <p className="text-[14px] font-bold text-white md:text-sm tracking-[-0.42px]">
      {card.title}
    </p>
    <p className="mt-0.5 text-[12px] text-white md:text-xs md:h-[48px] tracking-[-0.36px]">
      {card.desc}
    </p>
  </div>
);

const About_cpa_course = () => {
  return (
    /* Mobile puts each column on its own white card over a grey ground;
       desktop drops both and sits flat on white. */
    <section className="w-full bg-[#F6F6F6] py-5 md:bg-white md:py-10">
      <div className="main_container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)] lg:gap-x-8">
          <div className="flex flex-col gap-4 max-md:rounded-2xl max-md:bg-white max-md:p-4">
            <h2 className="text-2xl font-bold text-[#333] md:text-[32px]">
              What is <span style={{ color: "#39F" }}>CPA?</span>
            </h2>

            <div className="flex flex-col gap-3 text-[14px] text-[#333] font-medium tracking-[-0.42px] leading-normal">
              <p>
                CPA or Certified Public Accountant is the highest global
                accounting credential, issued by the American Institute of
                Certified Public Accountants (AICPA).
              </p>
              <p>
                A U.S. CPA license is your passport to work in accounting,
                auditing, tax, and finance roles across the United States and
                numerous key financial markets.
              </p>
              <p>
                Businesses are always on the lookout for trusted financial
                advisors, making a CPA course an ideal step for those aspiring
                to become global accountants.
              </p>
              <p className="text-[#333333] font-normal">
                In 2026, Accounting Has Changed. Hiring Has Changed.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
              {whatCards.map((c) => (
                <InfoCard key={c.title} {...c} />
              ))}
            </div>
            <InfoCard
              title={newHiringMetric.title}
              body={newHiringMetric.body}
              borderColor={newHiringMetric.borderColor}
            />
          </div>

          <ColumnDivider />

          <div className="flex flex-col gap-4 max-md:rounded-2xl max-md:bg-white max-md:p-4">
            <h2 className="text-2xl font-bold text-[#333] md:text-[32px]">
              Why Enroll <br className="md:hidden" />
              in a <span style={{ color: "#39F" }}>CPA Course?</span>
            </h2>

            <p className="text-[14px] text-[#333] font-medium tracking-[-0.42px] leading-normal">
              Regardless of whether you&apos;re a commerce graduate or a finance
              professional, a CPA qualification gives you:
            </p>

            {/* 2 columns on mobile, 3 on desktop; Pass Rates spans two of
                them at both sizes. See WhyCardData.layout for the ordering. */}
            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3">
              {whyCards.map((card) => (
                <WhyCard key={card.title} card={card} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About_cpa_course;
